import { Component, DestroyRef, signal, inject } from '@angular/core';

import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ProductSearchFacade } from '@boels/shared-state';
import { first, from, of } from 'rxjs';
import { filter, finalize, map, switchMap } from 'rxjs/operators';
import {
  Facet,
  FacetValue,
  ProductFilterGroupExpansionPanel,
  SearchCriteria,
} from '@boels/models';
import { BreakpointService, RoutingService, WindowRef } from '@boels/helpers';
import {
  faChevronDown,
  faFilter,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe, SlicePipe } from '@angular/common';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';

@Component({
  imports: [
    FontAwesomeModule,
    MatExpansionModule,
    MatCheckboxModule,
    TranslocoModule,
    AsyncPipe,
    SlicePipe,
    VerticalAlignerComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['productFilter'],
    },
    ProductSearchFacade,
  ],
  selector: 'boels-product-filter',
  templateUrl: './product-filter.component.html',
  styleUrls: ['./product-filter.component.scss'],
})
export class ProductFilterComponent {
  #productSearchFacade = inject(ProductSearchFacade);
  #breakpointService = inject(BreakpointService);
  #routingService = inject(RoutingService);
  #router = inject(Router);
  #windowRef = inject(WindowRef);
  #destroyRef = inject(DestroyRef);

  protected readonly isTabletAndDown$ = toSignal(
    this.#breakpointService.mdAndBelow$,
  );
  protected readonly faFilterIcon = faFilter;
  protected readonly faTimesIcon = faTimes;
  protected readonly faChevronDown = faChevronDown;
  protected $facets = this.getFacets();
  protected showClearFilters = false;
  protected $expansionPanelGroupData = signal<
    ProductFilterGroupExpansionPanel[]
  >([]);
  protected timeoutId: NodeJS.Timeout | undefined;

  constructor() {
    this.#destroyRef.onDestroy(() => {
      if (!this.timeoutId) return;
      clearTimeout(this.timeoutId);
    });
  }

  public onFilterChange(e: Event, facetValue: FacetValue): void {
    e.preventDefault();

    const facetQueryValue = facetValue.query?.query?.value ?? '';

    const queryParams: SearchCriteria = {
      query: facetQueryValue,
    };

    this.navigateWithScrollPositionPreserved(queryParams);
  }

  public onClearFilters(): void {
    this.navigateWithScrollPositionPreserved({ query: '' });
  }

  /**
   * This function is used to update the expansion panel data based on the group's name.
   *
   * @param name - The name of the group to update. This can be a string or `undefined`.
   * @param expanded - A boolean indicating whether the group is expanded or not.
   * @param initialize - A boolean indicating whether this is the initial setup. Default value is `false`.
   *
   * If `initialize` is `false` and the group exists, its `expanded` status is updated.
   * If the group doesn't exist, a new group is created with the provided `name` and `expanded` status.
   */
  public updateExpansionPanelData(
    name: string | undefined,
    expanded: boolean,
    initialize = false,
  ): void {
    const existingExpansionPanelGroup = this.findExpansionPanelGroup(name);

    if (!initialize && existingExpansionPanelGroup) {
      existingExpansionPanelGroup.expanded = expanded;
    } else {
      this.$expansionPanelGroupData.update((values) => {
        return [
          ...values,
          { name, expanded: this.$expansionPanelGroupData().length < 3 },
        ];
      });
    }
  }

  /**
   * This function is used to find a specific group in the `expansionPanelGroupData` array based on the group's name.
   *
   * @param name - The name of the group to find. This can be a string or `undefined`.
   * @returns The group from the `expansionPanelGroupData` array that matches the provided name. If no such group is found, it returns `undefined`.
   */
  public findExpansionPanelGroup(
    name: string | undefined,
  ): ProductFilterGroupExpansionPanel | undefined {
    return this.$expansionPanelGroupData().find((group) => group.name === name);
  }

  public isExpansionPanelExpanded(name: string | undefined): boolean {
    const existingExpansionPanelGroup = this.findExpansionPanelGroup(name);
    return existingExpansionPanelGroup
      ? existingExpansionPanelGroup.expanded
      : true;
  }

  private getFacets() {
    return this.#routingService.activatedRouteSnapshot$.pipe(
      filter(Boolean),
      switchMap((activatedRoute) => {
        const urlContainsSearch = activatedRoute?.url?.some(
          (urlSegment) => urlSegment.path === 'search',
        );
        return urlContainsSearch
          ? of(undefined)
          : !activatedRoute?.params?.['categoryId']
            ? of(undefined)
            : this.#productSearchFacade
                .getProductsByCategoryId(
                  activatedRoute?.params?.['categoryId'],
                  0,
                  activatedRoute?.queryParams?.['sortCode'],
                  activatedRoute?.queryParams?.['query'],
                )
                .pipe(
                  map((products) => {
                    this.showClearFilters =
                      products?.facets
                        ?.map((facet) =>
                          facet?.values?.some((value) => value.selected),
                        )
                        .some((facet) => facet) ?? false;

                    let facetsToReturn: Facet[] = [];

                    if (products && products.facets) {
                      facetsToReturn = products.facets.map((facet) => {
                        this.updateExpansionPanelData(facet.name, true, true);
                        if (
                          facet?.values?.length &&
                          facet?.values?.length > 6
                        ) {
                          return {
                            ...facet,
                            isExpandable: true,
                            isExpanded: facet.values?.some(
                              (value) =>
                                value.selected &&
                                facet.values &&
                                facet.values.indexOf(value) > 5,
                            ),
                          };
                        }
                        return { ...facet, isExpandable: false };
                      });
                    }
                    return facetsToReturn;
                  }),
                );
      }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  private navigateWithScrollPositionPreserved(
    queryParams: SearchCriteria,
  ): void {
    if (this.#windowRef.isBrowser()) {
      const currentScrollHeight = this.#windowRef.nativeWindow?.scrollY;
      from(
        this.#router.navigate([], {
          queryParams,
          queryParamsHandling: 'merge',
          relativeTo: this.#routingService.activatedRoute,
        }),
      )
        .pipe(
          first(),
          finalize(() => {
            if (!currentScrollHeight) return;
            this.timeoutId = setTimeout(() => {
              this.#windowRef.nativeWindow?.scrollTo({
                top: currentScrollHeight,
              });
            });
          }),
        )
        .subscribe();
    }
  }
}
